(function () {
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
    const setScrollbarWidth = () => {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const safeWidth = Math.max(scrollbarWidth, 0);
        document.documentElement.style.setProperty('--scrollbar-width', `${safeWidth}px`);
    };

    setScrollbarWidth();
    window.addEventListener('resize', setScrollbarWidth);

    // Track last input mode: disable hover UI flashes on hover-capable or emulated touch devices.
    let eegInputModeInitialized = false;
    const initInputMode = () => {
        if (eegInputModeInitialized) return;
        if (!document.body) return;
        eegInputModeInitialized = true;

        const setTouchMode = (enabled) => {
            document.body.classList.toggle('is-touch', Boolean(enabled));
        };

        const handlePointerDown = (event) => {
            const type = event && event.pointerType;
            if (type === 'touch' || type === 'pen') {
                setTouchMode(true);
                return;
            }
            if (type) {
                setTouchMode(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown, { passive: true });
        // Fallback when Pointer Events are unavailable (e.g., older browsers).
        document.addEventListener('touchstart', () => setTouchMode(true), { passive: true });
        // Allow hover/focus UI to work during keyboard navigation.
        document.addEventListener('keydown', (event) => {
            if (!event || event.key !== 'Tab') return;
            setTouchMode(false);
        });
    };

    initInputMode();
    document.addEventListener('DOMContentLoaded', initInputMode);

    // ========================================
    // Accessibility (a11y) utilities - migrated from Dawn
    // ========================================

    // Get all focusable elements within a container.
    function getFocusableElements(container) {
        return Array.from(
            container.querySelectorAll(
                "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
            )
        );
    }

    // Focus trap handler storage.
    const trapFocusHandlers = {};

    // Trap focus within a container (for modals, drawers, etc.).
    function trapFocus(container, elementToFocus = container) {
        const elements = getFocusableElements(container);
        const first = elements[0];
        const last = elements[elements.length - 1];

        removeTrapFocus();

        trapFocusHandlers.focusin = (event) => {
            if (event.target !== container && event.target !== last && event.target !== first) return;
            document.addEventListener('keydown', trapFocusHandlers.keydown);
        };

        trapFocusHandlers.focusout = () => {
            document.removeEventListener('keydown', trapFocusHandlers.keydown);
        };

        trapFocusHandlers.keydown = (event) => {
            if (event.code.toUpperCase() !== 'TAB') return;
            // On Tab at the last element, jump to the first.
            if (event.target === last && !event.shiftKey) {
                event.preventDefault();
                first.focus();
            }
            // On Shift+Tab at the first element, jump to the last.
            if ((event.target === container || event.target === first) && event.shiftKey) {
                event.preventDefault();
                last.focus();
            }
        };

        document.addEventListener('focusout', trapFocusHandlers.focusout);
        document.addEventListener('focusin', trapFocusHandlers.focusin);

        elementToFocus.focus();

        // If the focused element is an input, select its content.
        if (
            elementToFocus.tagName === 'INPUT' &&
            ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
            elementToFocus.value
        ) {
            elementToFocus.setSelectionRange(0, elementToFocus.value.length);
        }
    }

    // Remove focus trap.
    function removeTrapFocus(elementToFocus = null) {
        document.removeEventListener('focusin', trapFocusHandlers.focusin);
        document.removeEventListener('focusout', trapFocusHandlers.focusout);
        document.removeEventListener('keydown', trapFocusHandlers.keydown);
        if (elementToFocus) elementToFocus.focus();
    }

    // Close the Details element on Escape.
    function onKeyUpEscape(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        const openDetailsElement = event.target.closest('details[open]');
        if (!openDetailsElement) return;
        const summaryElement = openDetailsElement.querySelector('summary');
        openDetailsElement.removeAttribute('open');
        summaryElement.setAttribute('aria-expanded', false);
        summaryElement.focus();
    }

    // Pause all media playback.
    function pauseAllMedia() {
        document.querySelectorAll('.js-youtube').forEach((video) => {
            video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        document.querySelectorAll('.js-vimeo').forEach((video) => {
            video.contentWindow.postMessage('{"method":"pause"}', '*');
        });
        document.querySelectorAll('video').forEach((video) => video.pause());
    }

    // :focus-visible polyfill (only when not supported).
    try {
        document.querySelector(':focus-visible');
    } catch (e) {
        (function focusVisiblePolyfill() {
            const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN'];
            let currentFocusedElement = null;
            let mouseClick = null;

            window.addEventListener('keydown', (event) => {
                if (navKeys.includes(event.code.toUpperCase())) mouseClick = false;
            });

            window.addEventListener('mousedown', () => {
                mouseClick = true;
            });

            window.addEventListener('focus', () => {
                if (currentFocusedElement) currentFocusedElement.classList.remove('focused');
                if (mouseClick) return;
                currentFocusedElement = document.activeElement;
                currentFocusedElement.classList.add('focused');
            }, true);
        })();
    }

    // Expose to global scope for other scripts.
    window.trapFocus = trapFocus;
    window.removeTrapFocus = removeTrapFocus;
    window.pauseAllMedia = pauseAllMedia;
    window.getFocusableElements = getFocusableElements;
    window.onKeyUpEscape = onKeyUpEscape;

    // ========================================
    // Utility functions - migrated from Dawn
    // ========================================

    // Debounce function (cancelable).
    function debounceWithCancel(fn, wait) {
        let t;
        const debounced = (...args) => {
            clearTimeout(t);
            t = setTimeout(() => {
                t = null;
                fn.apply(this, args);
            }, wait);
        };
        debounced.cancel = () => {
            if (t) {
                clearTimeout(t);
                t = null;
            }
        };
        return debounced;
    }

    // Debounce function.
    function debounce(fn, wait) {
        return debounceWithCancel.call(this, fn, wait);
    }

    // Throttle function.
    function throttle(fn, delay) {
        let lastCall = 0;
        return function (...args) {
            const now = new Date().getTime();
            if (now - lastCall < delay) return;
            lastCall = now;
            return fn(...args);
        };
    }

    window.debounce = debounce;
    window.debounceWithCancel = debounceWithCancel;
    window.throttle = throttle;

    const loadStylesheetOnce = (src, options = {}) => {
        if (!src) return Promise.resolve();
        return new Promise((resolve, reject) => {
            const dataAttr = options.dataAttr || '';
            const media = options.media || '';
            const errorMessage = options.errorMessage || 'Stylesheet failed to load.';
            const setMediaOnLoad = options.setMediaOnLoad || null;
            const target = document.head || document.body;
            if (!target) {
                resolve();
                return;
            }

            let link = null;
            if (dataAttr) {
                link = document.querySelector(`link[${dataAttr}]`);
                if (link && link.rel !== 'stylesheet') {
                    link = null;
                }
            }
            if (!link) {
                link = document.querySelector(`link[rel="stylesheet"][href="${src}"]`);
                if (link && dataAttr) {
                    link.setAttribute(dataAttr, 'true');
                }
            }
            if (!link) {
                link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = src;
                if (media) {
                    link.media = media;
                }
                if (dataAttr) {
                    link.setAttribute(dataAttr, 'true');
                }
                target.appendChild(link);
            }

            const markLoaded = () => {
                if (setMediaOnLoad && link.getAttribute('media') === setMediaOnLoad.from) {
                    link.setAttribute('media', setMediaOnLoad.to);
                }
                if (link.dataset) {
                    link.dataset.eegLoaded = 'true';
                }
            };

            const isLoaded = link.dataset && link.dataset.eegLoaded === 'true';
            if (isLoaded || link.sheet) {
                markLoaded();
                resolve();
                return;
            }

            link.addEventListener('load', () => {
                markLoaded();
                resolve();
            }, { once: true });
            link.addEventListener('error', () => {
                reject(new Error(errorMessage));
            }, { once: true });
        });
    };

    window.EEGAssetLoader = window.EEGAssetLoader || {};
    if (typeof window.EEGAssetLoader.loadStylesheetOnce !== 'function') {
        window.EEGAssetLoader.loadStylesheetOnce = loadStylesheetOnce;
    }

    // Pub/Sub event bus.
    const subscribers = window.EEGCartSubscribers || {};
    const subscribe = window.subscribe || ((eventName, callback) => {
        if (!eventName || typeof callback !== 'function') return () => {};
        if (!subscribers[eventName]) {
            subscribers[eventName] = [];
        }
        subscribers[eventName].push(callback);
        return () => {
            subscribers[eventName] = (subscribers[eventName] || []).filter((fn) => fn !== callback);
        };
    });
    const publish = window.publish || ((eventName, payload) => {
        const callbacks = subscribers[eventName] || [];
        const results = callbacks.map((callback) => {
            try {
                return callback(payload);
            } catch (error) {
                return null;
            }
        });
        return Promise.all(results.map((result) => Promise.resolve(result)));
    });
    window.EEGCartSubscribers = subscribers;
    window.subscribe = subscribe;
    window.publish = publish;

    // Extend PUB_SUB_EVENTS (aligned with Dawn).
    window.PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {};
    window.PUB_SUB_EVENTS.cartUpdate = window.PUB_SUB_EVENTS.cartUpdate || 'cart-update';
    window.PUB_SUB_EVENTS.cartError = window.PUB_SUB_EVENTS.cartError || 'cart-error';
    window.PUB_SUB_EVENTS.quantityUpdate = window.PUB_SUB_EVENTS.quantityUpdate || 'quantity-update';
    window.PUB_SUB_EVENTS.variantChange = window.PUB_SUB_EVENTS.variantChange || 'variant-change';

    // ========================================
    // Web Components - migrated from Dawn
    // ========================================

    // QuantityInput - quantity +/- button component
    class QuantityInput extends HTMLElement {
        constructor() {
            super();
            this.input = this.querySelector('input');
            if (!this.input) return;
            this.changeEvent = new Event('change', { bubbles: true });
            this.input.addEventListener('change', this.onInputChange.bind(this));
            this.querySelectorAll('button').forEach((button) =>
                button.addEventListener('click', this.onButtonClick.bind(this))
            );
        }

        quantityUpdateUnsubscriber = undefined;

        connectedCallback() {
            this.validateQtyRules();
            if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.quantityUpdate) {
                this.quantityUpdateUnsubscriber = window.subscribe(
                    window.PUB_SUB_EVENTS.quantityUpdate,
                    this.validateQtyRules.bind(this)
                );
            }
        }

        disconnectedCallback() {
            if (this.quantityUpdateUnsubscriber) {
                this.quantityUpdateUnsubscriber();
            }
        }

        onInputChange() {
            this.validateQtyRules();
        }

        onButtonClick(event) {
            event.preventDefault();
            if (!this.input) return;
            const previousValue = this.input.value;
            const button = event.currentTarget;

            if (button && button.name === 'plus') {
                const minVal = parseInt(this.input.dataset.min || this.input.min || '0', 10);
                const stepVal = parseInt(this.input.step || '1', 10);
                if (minVal > stepVal && parseInt(this.input.value, 10) === 0) {
                    this.input.value = String(minVal);
                } else {
                    this.input.stepUp();
                }
            } else {
                this.input.stepDown();
            }

            if (previousValue !== this.input.value) {
                this.input.dispatchEvent(this.changeEvent);
            }
        }

        validateQtyRules() {
            if (!this.input) return;
            const value = parseInt(this.input.value, 10);
            const minVal = parseInt(this.input.min || '0', 10);
            const maxVal = parseInt(this.input.max || '0', 10);

            const buttonMinus = this.querySelector("button[name='minus'], .quantity__button[name='minus']");
            if (buttonMinus) {
                buttonMinus.classList.toggle('disabled', value <= minVal);
            }

            if (maxVal > 0) {
                const buttonPlus = this.querySelector("button[name='plus'], .quantity__button[name='plus']");
                if (buttonPlus) {
                    buttonPlus.classList.toggle('disabled', value >= maxVal);
                }
            }
        }
    }

    if (!customElements.get('quantity-input')) {
        customElements.define('quantity-input', QuantityInput);
    }

    const createArrowIcon = () => {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', 'icon icon-arrow-right');
        svg.setAttribute('width', '24');
        svg.setAttribute('height', '24');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('aria-hidden', 'true');

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M5 12H19M19 12L12 5M19 12L12 19');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '2');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        svg.appendChild(path);

        return svg;
    };

    if (typeof window.eegCreateArrowIcon !== 'function') {
        window.eegCreateArrowIcon = createArrowIcon;
    }

    // ========================================
    // Cart script lazy loading (P2-A)
    // ========================================

    const cartScriptsState = {
        loadPromise: null,
        prefetchDone: false,
    };

    const getCartScriptSources = () => {
        if (!document.body) return { cartSrc: '', cartDrawerSrc: '', cartUtilsSrc: '' };
        return {
            cartSrc: document.body.getAttribute('data-cart-src') || '',
            cartDrawerSrc: document.body.getAttribute('data-cart-drawer-src') || '',
            cartUtilsSrc: document.body.getAttribute('data-cart-utils-src') || '',
        };
    };

    const canPrefetchAssets = () => {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!connection) return true;
        if (connection.saveData) return false;
        if (typeof connection.effectiveType === 'string' && connection.effectiveType.includes('2g')) return false;
        return true;
    };

    if (typeof window.EEGAssetLoader.canPrefetchAssets !== 'function') {
        window.EEGAssetLoader.canPrefetchAssets = canPrefetchAssets;
    }

    const prefetchCartScripts = () => {
        if (cartScriptsState.prefetchDone) return;
        if (!document.head) return;
        if (!canPrefetchAssets()) return;

        const { cartSrc, cartDrawerSrc, cartUtilsSrc } = getCartScriptSources();
        if (!cartSrc || !cartDrawerSrc) return;

        cartScriptsState.prefetchDone = true;

        [cartUtilsSrc, cartSrc, cartDrawerSrc].forEach((href) => {
            if (!href) return;
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.as = 'script';
            link.href = href;
            link.setAttribute('data-eeg-cart-prefetch', 'true');
            document.head.appendChild(link);
        });
    };

    const loadExternalScriptOnce = (src, marker) => new Promise((resolve, reject) => {
        if (!document.body || !src) {
            resolve();
            return;
        }

        const existing = marker
            ? document.querySelector(`script[data-eeg-dynamic-script="${marker}"]`)
            : null;
        if (existing) {
            if (existing.getAttribute('data-eeg-loaded') === 'true') {
                resolve();
                return;
            }
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error(`${marker || 'script'} failed to load.`)), { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        if (marker) {
            script.setAttribute('data-eeg-dynamic-script', marker);
        }
        script.onload = () => {
            script.setAttribute('data-eeg-loaded', 'true');
            resolve();
        };
        script.onerror = () => reject(new Error(`${marker || 'script'} failed to load.`));
        document.body.appendChild(script);
    });

    const loadCartScripts = () => {
        if (window.EEGCartDrawer && typeof window.EEGCartDrawer.open === 'function') {
            return Promise.resolve();
        }

        if (cartScriptsState.loadPromise) return cartScriptsState.loadPromise;

        const { cartSrc, cartDrawerSrc, cartUtilsSrc } = getCartScriptSources();
        if (!cartSrc || !cartDrawerSrc) return Promise.resolve();

        const loadUtils = cartUtilsSrc
            ? loadExternalScriptOnce(cartUtilsSrc, 'cart-utils')
            : Promise.resolve();

        cartScriptsState.loadPromise = loadUtils
            .then(() => loadExternalScriptOnce(cartSrc, 'cart'))
            .then(() => loadExternalScriptOnce(cartDrawerSrc, 'cart-drawer'))
            .catch((error) => {
                cartScriptsState.loadPromise = null;
                throw error;
            });

        return cartScriptsState.loadPromise;
    };

    window.EEGCartScriptLoader = window.EEGCartScriptLoader || {};
    if (typeof window.EEGCartScriptLoader.load !== 'function') {
        window.EEGCartScriptLoader.load = loadCartScripts;
    }
    if (typeof window.EEGCartScriptLoader.prefetch !== 'function') {
        window.EEGCartScriptLoader.prefetch = prefetchCartScripts;
    }

    const ensureCartHelpers = () => {
        if (window.EEGCartAdd && typeof window.EEGCartAdd.addToCartFromForm === 'function') {
            return window.EEGCartAdd;
        }

        const addToCartFromForm = ({
            form,
            submitButton,
            errorContainer,
            source,
            onSuccess,
            onCart,
        }) => {
            if (!form) return Promise.resolve(null);
            if (submitButton && submitButton.disabled) return Promise.resolve(null);

            if (errorContainer) {
                errorContainer.textContent = '';
                errorContainer.hidden = true;
            }

            if (submitButton) {
                submitButton.disabled = true;
                submitButton.setAttribute('aria-busy', 'true');
            }

            return loadCartScripts()
                .catch(() => null)
                .then(() => {
                    const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
                    const cartDrawer = window.EEGCartDrawer;
                    const formData = new FormData(form);

                    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
                        const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
                        if (sections.length) {
                            formData.append('sections', sections);
                            formData.append('sections_url', window.location.pathname);
                        }
                        if (typeof cartDrawer.setActiveElement === 'function') {
                            cartDrawer.setActiveElement(document.activeElement);
                        }
                    }

                    return fetch(cartAddUrl, {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                        },
                        credentials: 'same-origin',
                        body: formData,
                    })
                        .then((response) => response.json())
                        .then((data) => {
                            if (data && data.status) {
                                throw data;
                            }

                            if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
                                const fallbackUrl = (window.routes && window.routes.cart_url) || '/cart';
                                window.location.href = fallbackUrl;
                                return null;
                            }

                            cartDrawer.renderContents(data);

                            if (window.publish && window.PUB_SUB_EVENTS) {
                                window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                                    source: source || 'add-to-cart',
                                    cartData: data,
                                    variantId: formData.get('id'),
                                });
                            }

                            if (typeof onSuccess === 'function') {
                                onSuccess(data);
                            }

                            if (typeof onCart === 'function') {
                                return fetch('/cart.js', {
                                    headers: { Accept: 'application/json' },
                                    credentials: 'same-origin',
                                })
                                    .then((response) => response.json())
                                    .then((cart) => {
                                        onCart(cart);
                                        return cart;
                                    })
                                    .catch(() => null);
                            }

                            return data;
                        })
                        .catch((error) => {
                            const message = error && error.description
                                ? error.description
                                : (error && error.message ? error.message : 'Add to cart failed.');

                            if (window.publish && window.PUB_SUB_EVENTS) {
                                window.publish(window.PUB_SUB_EVENTS.cartError, {
                                    source: source || 'add-to-cart',
                                    errors: error && error.errors ? error.errors : message,
                                    message,
                                });
                            }

                            if (errorContainer) {
                                errorContainer.textContent = message;
                                errorContainer.hidden = false;
                            }

                            return null;
                        })
                        .finally(() => {
                            if (submitButton) {
                                submitButton.disabled = false;
                                submitButton.removeAttribute('aria-busy');
                            }
                        });
                });
        };

        window.EEGCartAdd = { addToCartFromForm };
        return window.EEGCartAdd;
    };

    const baseFeaturesUiState = {
        loadPromise: null,
        prefetched: false,
        loaded: false,
    };

    const baseFeaturesOverlaysState = {
        loadPromise: null,
        prefetched: false,
        loaded: false,
    };

    const getBaseFeaturesUiSrc = () => {
        if (!document.body) return '';
        return document.body.getAttribute('data-base-features-ui-src') || '';
    };

    const getBaseFeaturesOverlaysSrc = () => {
        if (!document.body) return '';
        return document.body.getAttribute('data-base-features-overlays-src') || '';
    };

    const loadFeatureScript = (state, src, marker, errorMessage) => {
        if (state.loaded) return Promise.resolve();
        if (state.loadPromise) return state.loadPromise;
        if (!src || !document.body) return Promise.resolve();

        state.loadPromise = new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[${marker}]`);
            if (existing) {
                if (existing.getAttribute('data-eeg-loaded') === 'true') {
                    state.loaded = true;
                    resolve();
                    return;
                }
                existing.addEventListener('load', () => {
                    state.loaded = true;
                    resolve();
                }, { once: true });
                existing.addEventListener('error', () => {
                    state.loadPromise = null;
                    reject(new Error(errorMessage));
                }, { once: true });
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.defer = true;
            script.setAttribute(marker, 'true');
            script.onload = () => {
                script.setAttribute('data-eeg-loaded', 'true');
                state.loaded = true;
                resolve();
            };
            script.onerror = () => {
                state.loadPromise = null;
                reject(new Error(errorMessage));
            };
            document.body.appendChild(script);
        });

        return state.loadPromise;
    };

    const loadBaseFeaturesUi = () => loadFeatureScript(
        baseFeaturesUiState,
        getBaseFeaturesUiSrc(),
        'data-eeg-base-features-ui',
        'Base UI features failed to load.'
    );

    const loadBaseFeaturesOverlays = () => loadFeatureScript(
        baseFeaturesOverlaysState,
        getBaseFeaturesOverlaysSrc(),
        'data-eeg-base-features-overlays',
        'Base overlay features failed to load.'
    );

    const prefetchFeatureScript = (state, src, marker) => {
        if (state.prefetched) return;
        if (!src || !document.head) return;
        if (window.EEGAssetLoader && typeof window.EEGAssetLoader.canPrefetchAssets === 'function') {
            if (!window.EEGAssetLoader.canPrefetchAssets()) return;
        }
        state.prefetched = true;
        if (document.querySelector(`link[${marker}]`)) return;

        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'script';
        link.href = src;
        link.setAttribute(marker, 'true');
        document.head.appendChild(link);
    };

    const prefetchBaseFeaturesUi = () => prefetchFeatureScript(
        baseFeaturesUiState,
        getBaseFeaturesUiSrc(),
        'data-eeg-base-features-ui-prefetch'
    );

    const prefetchBaseFeaturesOverlays = () => prefetchFeatureScript(
        baseFeaturesOverlaysState,
        getBaseFeaturesOverlaysSrc(),
        'data-eeg-base-features-overlays-prefetch'
    );

    const overlayTriggerSelectors = [
        '[data-search-toggle]',
        '[data-cart-toggle]',
        '[data-quick-add-url]',
    ];

    const uiTriggerSelectors = [
        '.header__menu-toggle',
    ];

    const findFeatureTrigger = (target) => {
        if (!(target instanceof Element)) return null;
        for (const selector of overlayTriggerSelectors) {
            const trigger = target.closest(selector);
            if (trigger) return trigger;
        }
        for (const selector of uiTriggerSelectors) {
            const trigger = target.closest(selector);
            if (trigger) return trigger;
        }
        return null;
    };

    const getTriggerType = (trigger) => {
        if (!trigger) return '';
        if (trigger.closest('.header__menu-toggle')) return 'ui';
        if (trigger.closest('[data-search-toggle]') || trigger.closest('[data-cart-toggle]') || trigger.closest('[data-quick-add-url]')) {
            return 'overlays';
        }
        return '';
    };

    const shouldBypassFeatureLoad = (trigger) => {
        if (!trigger) return false;
        if (trigger.getAttribute('data-eeg-bypass-feature-load') === 'true') {
            trigger.removeAttribute('data-eeg-bypass-feature-load');
            return true;
        }
        return false;
    };

    const fallbackForTrigger = (trigger) => {
        if (!trigger) return;
        if (trigger.closest('[data-search-toggle]')) {
            const fallbackUrl = (window.routes && window.routes.search_url) || '/search';
            window.location.href = fallbackUrl;
            return;
        }
        if (trigger.closest('[data-cart-toggle]')) {
            const fallbackUrl = (window.routes && window.routes.cart_url) || '/cart';
            window.location.href = fallbackUrl;
            return;
        }
        if (trigger.closest('[data-quick-add-url]')) {
            const rawUrl = trigger.getAttribute('data-quick-add-url');
            if (!rawUrl) return;
            try {
                const url = new URL(rawUrl, window.location.origin);
                url.searchParams.delete('section_id');
                window.location.href = `${url.pathname}${url.search}`;
            } catch (error) {
                window.location.href = rawUrl;
            }
        }
    };

    const handleFeatureTrigger = (event) => {
        const trigger = findFeatureTrigger(event.target);
        if (!trigger) return;
        if (shouldBypassFeatureLoad(trigger)) return;
        if (event.type === 'keydown') {
            const code = (event.code || '').toUpperCase();
            if (code !== 'SPACE' && code !== 'ENTER') return;
        }

        const triggerType = getTriggerType(trigger);
        if (triggerType === 'ui') {
            if (baseFeaturesUiState.loaded) return;
            if (!getBaseFeaturesUiSrc()) return;
            event.preventDefault();
            event.stopPropagation();
            loadBaseFeaturesUi()
                .then(() => {
                    trigger.click();
                })
                .catch(() => {
                    trigger.setAttribute('data-eeg-bypass-feature-load', 'true');
                    trigger.click();
                });
            return;
        }

        if (triggerType === 'overlays') {
            if (baseFeaturesOverlaysState.loaded) return;
            if (!getBaseFeaturesOverlaysSrc()) return;
            event.preventDefault();
            event.stopPropagation();
            loadBaseFeaturesOverlays()
                .then(() => {
                    trigger.click();
                })
                .catch(() => {
                    fallbackForTrigger(trigger);
                });
        }
    };

    const handleFeaturePrefetch = (event) => {
        const trigger = findFeatureTrigger(event.target);
        if (!trigger) return;
        const triggerType = getTriggerType(trigger);
        if (triggerType === 'ui') {
            if (baseFeaturesUiState.prefetched || baseFeaturesUiState.loaded) return;
            prefetchBaseFeaturesUi();
            return;
        }
        if (triggerType === 'overlays') {
            if (baseFeaturesOverlaysState.prefetched || baseFeaturesOverlaysState.loaded) return;
            prefetchBaseFeaturesOverlays();
        }
    };

    const scheduleBaseFeaturesUiLoad = () => {
        if (!getBaseFeaturesUiSrc()) return;
        const load = () => {
            loadBaseFeaturesUi().catch(() => {});
        };
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(load, { timeout: 2500 });
        } else {
            window.setTimeout(load, 1500);
        }
    };

    const runWhenReady = (fn) => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn);
        } else {
            fn();
        }
    };

    ensureCartHelpers();

    runWhenReady(() => {
        document.addEventListener('click', handleFeatureTrigger, true);
        document.addEventListener('keydown', handleFeatureTrigger);
        document.addEventListener('pointerover', handleFeaturePrefetch, { passive: true });
        document.addEventListener('focusin', handleFeaturePrefetch);
        scheduleBaseFeaturesUiLoad();
    });
})();
